import { useContext, createContext, useMemo, useCallback } from "react"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Creators as ApiWrapperCreators } from "model/apiWrapper"
import { Creators as PreferencesCreators } from "model/preferences"
import { useAccountId } from "resources/account"
import { useLastPreferenceRedirect } from "resources/preferences"
import { usePostQuery } from "./queries"
import { get } from "lodash"
import { combineTanStackStatuses } from "helpers/data-statuses"

const PreferenceContext = createContext(null)

export default PreferenceContext

function getContextPreferenceQueryKey(accountId, contextKey, contextType, subType) {
  return ["preferences", "context", accountId, contextKey ?? "", contextType ?? "", subType].join(":")
}

export function useSaveContextPreferences(subType) {
  const context = useContext(PreferenceContext)
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const readQueryKey = useMemo(
    () => [getContextPreferenceQueryKey(context?.accountId, context?.key, context?.type, subType)],
    [context?.accountId, context?.key, context?.type, subType],
  )
  const savePreferenceMutation = useMutation({
    mutationFn: (value) => {
      const payload = {
        items: [{
          accountId: context.accountId,
          key: context.key,
          type: `${context.type}.${subType}`,
          value,
        }],
      }
      return new Promise((resolve, reject) => {
        dispatch(ApiWrapperCreators.put("/api/users/me/preferences", payload, resolve, reject))
      })
    },
    onSuccess: (_data, savedValue) => {
      queryClient.setQueryData(readQueryKey, [{ value: savedValue }])
    },
  })

  return savePreferenceMutation.mutate
}

export function saveContextPreferencesDual(primarySubType, mirrorSubType, savePrimary, saveMirror, value) {
  savePrimary(value)
  if (mirrorSubType != null && mirrorSubType !== primarySubType) {
    saveMirror(value)
  }
}

export function useSaveContextPreferencesDual(primarySubType, mirrorSubType) {
  const savePrimary = useSaveContextPreferences(primarySubType)
  const saveMirror = useSaveContextPreferences(mirrorSubType ?? primarySubType)

  return useCallback(
    (value) => {
      saveContextPreferencesDual(primarySubType, mirrorSubType, savePrimary, saveMirror, value)
    },
    [savePrimary, saveMirror, mirrorSubType, primarySubType],
  )
}

export function useReadContextPreference(subType, defaultValue, options = {}) {
  const { enabled = true } = options
  const accountId = useAccountId()
  const context = useContext(PreferenceContext)
  const queryKey = useMemo(
    () => ["preferences", "context", accountId, context?.key ?? "", context?.type ?? "", subType].join(":"),
    [accountId, context?.key, context?.type, subType],
  )

  const queryResults = usePostQuery({
    url: "/api/users/me/preferences",
    payload: {
      items: ([{
        accountId,
        key: context.key,
        type: `${context.type}.${subType}`,
      }]),
    },
    key: queryKey,
    enabled: enabled && Boolean(accountId),
    select: (queryResult) => get(queryResult, "0.value", defaultValue),
  })

  const queryStatus = combineTanStackStatuses([queryResults])

  return [queryResults.data, queryStatus]
}

/**
 * Saves a user preference scoped to the current account.
 */
export function useSavePreference(key, type) {
  const accountId = useAccountId()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const readQueryKey = useMemo(
    () => [["preferences", accountId, key, type].join(":")],
    [accountId, key, type],
  )
  const savePreferenceMutation = useMutation({
    mutationFn: (value) => {
      const payload = {
        items: [{
          accountId,
          key,
          type,
          value,
        }],
      }
      return new Promise((resolve, reject) => {
        dispatch(ApiWrapperCreators.put("/api/users/me/preferences", payload, resolve, reject))
      })
    },
    onSuccess: (_data, savedValue) => {
      queryClient.setQueryData(readQueryKey, [{ value: savedValue }])
    },
  })

  return savePreferenceMutation.mutate
}

/**
 * Retrieves a user preference scoped to the current account.
 */
export function useReadPreference(key, type, defaultValue) {
  const accountId = useAccountId()
  const queryKey = useMemo(
    () => ["preferences", accountId, key, type].join(":"),
    [accountId, key, type],
  )

  const queryResults = usePostQuery({
    url: "/api/users/me/preferences",
    payload: {
      items: ([{
        accountId,
        key,
        type,
      }]),
    },
    key: queryKey,
    enabled: Boolean(accountId),
    select: (queryResult) => get(queryResult, "0.value", defaultValue),
  })

  const queryStatus = combineTanStackStatuses([queryResults])

  return [queryResults.data, queryStatus]
}

export function useCheckLastPreferenceRedirect() {
  const accountId = useAccountId()
  const { lastPreferenceRedirect } = useLastPreferenceRedirect()
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const wasRedirectedFromPreferences = useMemo(() => lastPreferenceRedirect && location.pathname.startsWith(lastPreferenceRedirect.redirectedTo), [lastPreferenceRedirect, location])

  return {
    wasRedirectedFromPreferences,
    deletePreferenceAndRedirectToDefault: () => {
      if (lastPreferenceRedirect?.preferenceType) {
        dispatch(PreferencesCreators.deletePreferences([{ type: lastPreferenceRedirect.preferenceType, accountId }]))
        if (lastPreferenceRedirect?.defaultRedirectTo) {
          navigate(lastPreferenceRedirect.defaultRedirectTo, { replace: true })
        }
      }
    },
  }
}
